import { toast } from 'react-toastify'
import { DATA_MODEL_SCHEMA_RESOURCE_KINDS } from 'common/types/kraken-core/DataModelSchemaResource'
import {
  useGenerateX12Schema,
  useListDataModelSchemaResources,
  useDownloadDataModelSchemaResource,
  useCreateDataModelSchemaResource,
  useUpdateDataModelSchema,
  X12SchemaGenerationRequest,
  X12SchemaGenerationResponse,
} from '../api'
import { safeBase64Encode } from 'common/helpers/encoding'

export interface FileWithSource {
  file: File
  source: 'existing' | 'uploaded'
  originalResourceId?: string
}

export interface X12SchemaGenerationService {
  loadExistingKnowledgeResources: () => Promise<FileWithSource[]>
  loadSchemaGenerationPrompt: () => Promise<string | null>
  acceptGeneratedSchema: (
    modelId: string,
    customizations: string,
    generatedSchema: any
  ) => Promise<void>
  generateSchema: (
    request: X12SchemaGenerationRequest
  ) => Promise<[X12SchemaGenerationResponse | null, Error | null]>
  isLoadingResources: boolean
  isGenerating: boolean
  existingResources: any
}

export const useX12SchemaGenerationService = (modelId?: string): X12SchemaGenerationService => {
  const generateX12SchemaMutation = useGenerateX12Schema()
  const downloadResourceMutation = useDownloadDataModelSchemaResource()
  const createResourceMutation = useCreateDataModelSchemaResource()
  const updateSchemaMutation = useUpdateDataModelSchema()

  // Fetch existing knowledge resources
  const {
    data: existingResources,
    isLoading: isLoadingResources,
  } = useListDataModelSchemaResources(
    {
      model_id: modelId || '',
    },
    {
      enabled: !!modelId,
    }
  )

  const loadExistingKnowledgeResources = async (): Promise<FileWithSource[]> => {
    if (!existingResources?.results || !modelId) {
      return []
    }

    try {
      const knowledgeResources = existingResources.results.filter(
        resource => resource.kind === DATA_MODEL_SCHEMA_RESOURCE_KINDS.KNOWLEDGE
      )

      const filePromises = knowledgeResources.map(async resource => {
        try {
          const blob = await downloadResourceMutation.mutateAsync(resource.id)
          const file = new File([blob], resource.name, { type: resource.content_type })
          return {
            file,
            source: 'existing' as const,
            originalResourceId: resource.id,
          } as FileWithSource
        } catch (error) {
          console.error(`Failed to download resource ${resource.name}:`, error)
          return null
        }
      })

      const files = await Promise.all(filePromises)
      const validFiles = files.filter(file => file !== null) as FileWithSource[]

      return validFiles
    } catch (error) {
      console.error('Failed to load existing knowledge resources:', error)
      toast.error('Failed to load existing resources')
      return []
    }
  }

  const loadSchemaGenerationPrompt = async (): Promise<string | null> => {
    if (!existingResources?.results || !modelId) {
      return null
    }

    try {
      const schemaGenerationRulesResource = existingResources.results.find(
        resource =>
          resource.name === '__schema_generation_prompt.txt' &&
          resource.kind === DATA_MODEL_SCHEMA_RESOURCE_KINDS.INTERNAL
      )

      if (schemaGenerationRulesResource) {
        const blob = await downloadResourceMutation.mutateAsync(schemaGenerationRulesResource.id)
        const content = await blob.text()
        return content
      }

      return null
    } catch (error) {
      console.error('Failed to load schema generation prompt:', error)
      return null
    }
  }

  const acceptGeneratedSchema = async (
    modelId: string,
    customizations: string,
    generatedSchema: any
  ): Promise<void> => {
    if (!modelId) {
      throw new Error('Model ID is required to accept generated schema')
    }

    try {
      // First, save the generated schema to the structure field of the DataModelSchema
      await updateSchemaMutation.mutateAsync({
        id: modelId,
        structure: safeBase64Encode(JSON.stringify(generatedSchema)),
      })

      // Then, create the schema generation prompt file as an INTERNAL resource
      const promptFile = new File([customizations], '__schema_generation_prompt.txt', {
        type: 'text/plain',
      })

      await createResourceMutation.mutateAsync({
        model: modelId,
        name: '__schema_generation_prompt.txt',
        file: promptFile,
        content_type: 'text/plain',
        encoding: 'utf-8',
        kind: DATA_MODEL_SCHEMA_RESOURCE_KINDS.INTERNAL,
      })

      toast.success('Schema accepted and saved successfully')
    } catch (error) {
      console.error('Failed to accept generated schema:', error)
      toast.error('Failed to accept generated schema')
      throw error
    }
  }

  const generateSchema = async (
    request: X12SchemaGenerationRequest
  ): Promise<[X12SchemaGenerationResponse | null, Error | null]> => {
    try {
      console.log('Service layer: Calling generateX12SchemaMutation.mutateAsync')
      const [response, error] = await generateX12SchemaMutation.mutateAsync(request)
      console.log('Service layer: Received response:', response, 'error:', error)

      if (error) {
        console.error('Service layer: Error from API:', error)
        return [null, error]
      }

      return [response, null]
    } catch (error) {
      console.error('Service layer: Failed to generate schema:', error)
      return [null, error instanceof Error ? error : new Error(String(error))]
    }
  }

  return {
    loadExistingKnowledgeResources,
    loadSchemaGenerationPrompt,
    acceptGeneratedSchema,
    generateSchema,
    isLoadingResources,
    isGenerating: generateX12SchemaMutation.isLoading,
    existingResources: existingResources?.results || [],
  }
}
